import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import LogsVazio from "../../Compartilhado/LogsVazio";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

interface CampoAlterado {
    campo: string;
    antes: any;
    depois: any;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
        'Pendente': 'Pendente',
        'Em Triagem': 'Em Triagem',
        'Em Contratação': 'Em Contratação',
        'Aguardando Execução': 'Aguardando Execução',
        'Em Execução': 'Em Execução',
        'Em Conferência': 'Em Conferência',
        'Finalizada': 'Finalizada',
        'Cancelada': 'Cancelada',
        'Interrompida': 'Interrompida'
    };
    return statusMap[status] || status;
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'status': 'Status',
        'profissional_responsavel': 'Profissional responsável'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const compararDados = (dadosAntes: any, dadosDepois: any, dadosExtra: any): CampoAlterado[] => {
    const campos: CampoAlterado[] = [];

    if (dadosDepois?.status && dadosAntes?.status !== dadosDepois.status) {
        campos.push({
            campo: 'status',
            antes: getStatusLabel(dadosAntes?.status || 'Aguardando Execução'),
            depois: getStatusLabel(dadosDepois.status)
        });
    }

    if (dadosExtra?.profissional_responsavel) {
        campos.push({
            campo: 'profissional_responsavel',
            antes: null,
            depois: dadosExtra.profissional_responsavel
        });
    } else if (dadosDepois?.nome_correspondente || dadosDepois?.correspondente_nome || dadosDepois?.nome_profissional) {
        const nomeProfissional = dadosDepois.nome_correspondente || dadosDepois.correspondente_nome || dadosDepois.nome_profissional;
        const idProfissional = dadosDepois.id_usuario || dadosDepois.id_profissional;
        const profissionalInfo = idProfissional ? `${nomeProfissional} (ID: ${idProfissional})` : nomeProfissional;
        
        campos.push({
            campo: 'profissional_responsavel',
            antes: null,
            depois: profissionalInfo
        });
    }

    return campos;
};

export default function ExpandedContentDemandaIniciada({ 
    item, 
    onNavigateToFile 
}: ExpandedContentProps) {
    const [camposAlteracoes, setCamposAlteracoes] = useState<CampoAlterado[]>([]);
    const [camposPrincipal, setCamposPrincipal] = useState<CampoAlterado[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');

                    if (logProposta) {
                        let dadosAntes = logProposta.dados_antes;
                        let dadosDepois = logProposta.dados_depois;
                        let dadosExtra = logProposta.dados_extra;

                        if (typeof dadosAntes === 'string') {
                            try {
                                dadosAntes = JSON.parse(dadosAntes);
                            } catch (e) {
                                dadosAntes = null;
                            }
                        }

                        if (typeof dadosDepois === 'string') {
                            try {
                                dadosDepois = JSON.parse(dadosDepois);
                            } catch (e) {
                                dadosDepois = null;
                            }
                        }

                        if (typeof dadosExtra === 'string') {
                            try {
                                dadosExtra = JSON.parse(dadosExtra);
                            } catch (e) {
                                dadosExtra = null;
                            }
                        }

                        const todosCampos = compararDados(dadosAntes, dadosDepois, dadosExtra);
                        
                        const alteracoes = todosCampos.filter(c => c.campo === 'status');
                        const principal = todosCampos.filter(c => c.campo !== 'status');
                        
                        setCamposAlteracoes(alteracoes);
                        setCamposPrincipal(principal);
                    } else {
                        setCamposAlteracoes([]);
                        setCamposPrincipal([]);
                    }
                } else {
                    setCamposAlteracoes([]);
                    setCamposPrincipal([]);
                }
            } catch (error: any) {
                setCamposAlteracoes([]);
                setCamposPrincipal([]);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposAlteracoes.length === 0 && camposPrincipal.length === 0;

    if (semDados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            {camposAlteracoes.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Principal</h4>
                        <TableContainer>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="field-col">Campo</th>
                                        <th className="before-col">Antes</th>
                                        <th className="after-col">Depois</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {camposAlteracoes.map((campo, index) => (
                                        <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                            <td className="field-cell">{getFieldLabel(campo.campo)}</td>
                                            <td className="before-cell">{formatValue(campo.antes)}</td>
                                            <td className="after-cell">
                                                <div className="after-content">
                                                    <span className="highlight-bar" />
                                                    <span>{formatValue(campo.depois)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TableContainer>
                    </div>
                </>
            )}

            {camposPrincipal.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Principal</h4>
                        <TableContainer>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="field-col">Campo</th>
                                        <th className="before-col">Antes</th>
                                        <th className="after-col">Depois</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {camposPrincipal.map((campo, index) => (
                                        <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                            <td className="field-cell">{getFieldLabel(campo.campo)}</td>
                                            <td className="before-cell">{formatValue(campo.antes)}</td>
                                            <td className="after-cell">
                                                <div className="after-content">
                                                    <span className="highlight-bar" />
                                                    <span>{formatValue(campo.depois)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TableContainer>
                    </div>
                </>
            )}
        </ExpandedContent>
    );
}

